// frontend/src/components/DevisList.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import api from '../api';
import { exportDevis } from '../utils/exportExcel';

const STATUTS = ['brouillon', 'envoye', 'accepte', 'refuse'];

const STATUT_BADGE = {
  brouillon: { bg: 'secondary', label: 'Brouillon' },
  envoye:    { bg: 'primary',   label: 'Envoyé' },
  accepte:   { bg: 'success',   label: 'Accepté' },
  refuse:    { bg: 'danger',    label: 'Refusé' },
};

const EMPTY_FORM = {
  chantier_id:         '',
  client_id:           '',
  statut:              'brouillon',
  date_creation:       new Date().toISOString().substring(0, 10),
  date_validite:       '',
  montant_ht:          '',
  tva:                 20,
  conditions_paiement: '',
  notes:               ''
};

function DevisList({ onDevisChange }) {
  const [devis, setDevis]             = useState([]);
  const [chantiers, setChantiers]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [showModal, setShowModal]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [editDevis, setEditDevis]     = useState(null);
  const [devisToDelete, setDevisToDelete] = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [submitting, setSubmitting]   = useState(false);
  const [filtreStatut, setFiltreStatut] = useState('');
  const [pdfLoading, setPdfLoading]   = useState(null);
  const [sendLoading, setSendLoading]   = useState(null);

  const montantTTC = form.montant_ht && form.tva
    ? (parseFloat(form.montant_ht) * (1 + parseFloat(form.tva) / 100)).toFixed(2)
    : '0.00';

  const fetchDevis = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = filtreStatut ? `?statut=${filtreStatut}` : '';
      const response = await api.get(`/devis${params}`);
      setDevis(response.data);
      if (onDevisChange) onDevisChange(response.data.length);
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [filtreStatut]);

  const fetchChantiers = useCallback(async () => {
    try {
      const response = await api.get('/chantiers');
      setChantiers(response.data);
    } catch (err) {
      console.error('Erreur chargement chantiers:', err);
    }
  }, []);

  useEffect(() => { fetchDevis(); },    [fetchDevis]);
  useEffect(() => { fetchChantiers(); }, [fetchChantiers]);

  const handleChantierChange = (e) => {
    const chantier_id = e.target.value;
    const chantier    = chantiers.find(c => c.id === parseInt(chantier_id));
    setForm({ ...form, chantier_id, client_id: chantier ? chantier.client_id : '' });
  };

  const handleField = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const openCreateModal = () => {
    setEditDevis(null);
    setForm(EMPTY_FORM);
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (d) => {
    setEditDevis(d);
    setForm({
      chantier_id:         d.chantier_id,
      client_id:           d.client_id,
      statut:              d.statut,
      date_creation:       d.date_creation ? d.date_creation.substring(0, 10) : '',
      date_validite:       d.date_validite  ? d.date_validite.substring(0, 10)  : '',
      montant_ht:          d.montant_ht  || '',
      tva:                 d.tva         || 20,
      conditions_paiement: d.conditions_paiement || '',
      notes:               d.notes || ''
    });
    setError(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (editDevis) {
        await api.put(`/devis/${editDevis.id}`, form);
      } else {
        await api.post('/devis', form);
      }
      setShowModal(false);
      await fetchDevis();
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/devis/${devisToDelete.id}`);
      setShowConfirm(false);
      setDevisToDelete(null);
      await fetchDevis();
    } catch (err) {
      setShowConfirm(false);
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    }
  };

  const handlePDF = async (d) => {
    setPdfLoading(d.id);
    try {
      const response = await api.get(`/devis/${d.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${d.numero_devis}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Erreur PDF: ${err.response?.data?.error || err.message}`);
    } finally {
      setPdfLoading(null);
    }
  };

  const handleEnvoyer = async (d) => {
    if (!window.confirm(`Envoyer le devis ${d.numero_devis} par email au client ?`)) return;
    setSendLoading(d.id);
    try {
      const res = await fetch(`/api/notifications/devis/${d.id}/envoyer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur envoi');
      alert(data.message);
      await fetchDevis();
    } catch (err) {
      setError(`Erreur: ${err.message}`);
    } finally {
      setSendLoading(null);
    }
  };

  const handleStatut = async (d, statut) => {
    const label = statut === 'accepte' ? 'accepté' : 'refusé';
    if (!window.confirm(`Marquer le devis ${d.numero_devis} comme ${label} ?`)) return;
    try {
      const res = await fetch(`/api/notifications/devis/${d.id}/statut`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ statut })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      await fetchDevis();
    } catch (err) {
      setError(`Erreur: ${err.message}`);
    }
  };

  const formatMontant = (val) =>
    val ? `${Number(val).toLocaleString('fr-FR')} €` : '-';

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Chargement des devis...</p>
      </div>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="danger" className="mt-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0">Devis ({devis.length})</h4>
          <Form.Select size="sm" style={{ width: '160px' }}
            value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}>
            <option value="">Tous les statuts</option>
            {STATUTS.map(s => (
              <option key={s} value={s}>{STATUT_BADGE[s].label}</option>
            ))}
          </Form.Select>
        </div>
        <Button variant="outline-success" className="me-2" onClick={() => exportDevis(devis)}>📊 Excel</Button>
        <Button variant="primary" onClick={openCreateModal}>+ Nouveau Devis</Button>
      </div>

      {devis.length === 0 ? (
        <Alert variant="info">Aucun devis pour le moment.</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th>N°</th>
              <th>Référence</th>
              <th>Chantier</th>
              <th>Client</th>
              <th>Statut</th>
              <th>Montant HT</th>
              <th>TVA</th>
              <th>Montant TTC</th>
              <th>Validité</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {devis.map((d) => (
              <tr key={d.id}>
                <td>{d.id}</td>
                <td><strong>{d.numero_devis}</strong></td>
                <td>{d.chantier_nom || '-'}</td>
                <td>
                  {d.client_nom}
                  {d.client_entreprise && (
                    <div><small className="text-muted">{d.client_entreprise}</small></div>
                  )}
                </td>
                <td>
                  <Badge bg={STATUT_BADGE[d.statut]?.bg || 'secondary'}>
                    {STATUT_BADGE[d.statut]?.label || d.statut}
                  </Badge>
                </td>
                <td>{formatMontant(d.montant_ht)}</td>
                <td>{d.tva} %</td>
                <td><strong>{formatMontant(d.montant_ttc)}</strong></td>
                <td>{d.date_validite ? d.date_validite.substring(0, 10) : '-'}</td>
                <td>
                  <div className="d-flex gap-1">
                    <Button size="sm" variant="outline-success"
                      onClick={() => handlePDF(d)}
                      disabled={pdfLoading === d.id}
                      title="Télécharger PDF">
                      {pdfLoading === d.id ? '⏳' : '📄'} PDF
                    </Button>
                    {(d.statut === 'brouillon' || d.statut === 'envoye') && (
                      <Button size="sm" variant="outline-info"
                        onClick={() => handleEnvoyer(d)}
                        disabled={sendLoading === d.id}
                        title="Envoyer au client par email">
                        {sendLoading === d.id ? '⏳' : '📧'}
                      </Button>
                    )}
                    {d.statut === 'envoye' && (
                      <>
                        <Button size="sm" variant="success"
                          onClick={() => handleStatut(d, 'accepte')}
                          title="Marquer comme accepté">✅</Button>
                        <Button size="sm" variant="outline-danger"
                          onClick={() => handleStatut(d, 'refuse')}
                          title="Marquer comme refusé">❌</Button>
                      </>
                    )}
                    <Button size="sm" variant="outline-primary"
                      onClick={() => openEditModal(d)}>✏️</Button>
                    {d.statut === 'brouillon' && (
                      <Button size="sm" variant="outline-danger"
                        onClick={() => { setDevisToDelete(d); setShowConfirm(true); }}>🗑️</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editDevis ? `✏️ Modifier ${editDevis.numero_devis}` : '📋 Nouveau Devis'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <div className="row">
              <div className="col-md-8">
                <Form.Group className="mb-3">
                  <Form.Label>Chantier *</Form.Label>
                  <Form.Select name="chantier_id" required value={form.chantier_id}
                    onChange={handleChantierChange} disabled={!!editDevis}>
                    <option value="">-- Sélectionner un chantier --</option>
                    {chantiers.map(c => (
                      <option key={c.id} value={c.id}>{c.nom} — {c.client_nom}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Statut</Form.Label>
                  <Form.Select name="statut" value={form.statut} onChange={handleField}>
                    {STATUTS.map(s => (
                      <option key={s} value={s}>{STATUT_BADGE[s].label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Montant HT (€) *</Form.Label>
                  <Form.Control type="number" name="montant_ht" min="0" step="0.01"
                    required value={form.montant_ht} onChange={handleField} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>TVA (%)</Form.Label>
                  <Form.Select name="tva" value={form.tva} onChange={handleField}>
                    <option value="0">0 % (exonéré)</option>
                    <option value="5.5">5.5 % (réduit)</option>
                    <option value="10">10 % (intermédiaire)</option>
                    <option value="20">20 % (normal)</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Montant TTC (€)</Form.Label>
                  <Form.Control type="text"
                    value={`${Number(montantTTC).toLocaleString('fr-FR')} €`}
                    disabled className="bg-light fw-bold" />
                </Form.Group>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Date de création</Form.Label>
                  <Form.Control type="date" name="date_creation"
                    value={form.date_creation} onChange={handleField} />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Date de validité</Form.Label>
                  <Form.Control type="date" name="date_validite"
                    value={form.date_validite} onChange={handleField} />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Conditions de paiement</Form.Label>
              <Form.Control type="text" name="conditions_paiement"
                placeholder="ex: 30% à la commande, 70% à la livraison"
                value={form.conditions_paiement} onChange={handleField} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control as="textarea" rows={2} name="notes"
                value={form.notes} onChange={handleField} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting
                ? (editDevis ? 'Mise à jour...' : 'Création...')
                : (editDevis ? 'Mettre à jour' : 'Créer le devis')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>⚠️ Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Supprimer le devis <strong>{devisToDelete?.numero_devis}</strong> ?
          <Alert variant="warning" className="mt-3 mb-0">
            Seuls les devis en brouillon peuvent être supprimés.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>Annuler</Button>
          <Button variant="danger" onClick={handleDelete}>🗑️ Supprimer</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default DevisList;
